<template>
  <div>
    <div ref="printPdf">
      <!-- Header -->
      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div>
            <p style="font-size: 18px; font-weight: bold; margin: 0 0 4px 0; color: #333;">
              BAY ATLANTIC UNIVERSITY
            </p>
            <p style="font-size: 16px; font-weight: bold; margin: 0 0 4px 0; color: #333;">
              Dual Enrollment Student Enrollment Agreement
            </p>
            <p style="font-size: 13px; margin: 0; color: #555;">
              <strong>(Revision Date: 07/28/2025)</strong>
            </p>
          </div>
          <div style="flex-shrink: 0;">
            <img
                src="../../../assets/img/BAU_Logo.png"
                alt="BAU Logo"
                style="height: 60px; width: auto;"
                ref="logoImage"
            />
          </div>
        </div>
        <hr style="margin: 16px 0;">
        <div class="mb-3 mt-3">
          <strong style="font-size: 12px;">
            BY CLICKING THE BOX AT THE END OF THIS DOCUMENT INDICATING YOUR ACCEPTANCE (THE “ACCEPTANCE”) OF THIS
            ENROLLMENT CONTRACT (THIS “AGREEMENT”), YOU AGREE YOU HAVE READ AND ARE BOUND BY THE TERMS OF THIS
            AGREEMENT. IF YOU DO NOT AGREE WITH THIS AGREEMENT, YOU MUST NOT ACCEPT THIS AGREEMENT AND MUST NOT CONTINUE
            WITH THE COURSE REGISTRATION. DOING SO MEANS YOU START AN OFFICIAL WITHDRAWAL PROCESS. IF YOU ACCEPT THIS
            AGREEMENT, YOU ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD, AND ARE BOUND BY, THE TERMS AND CONDITIONS
            STATED HEREIN.
          </strong>
        </div>
        <div class="mb-4" style="font-size: 12px;">
          <p>
            This Agreement is by and between you and BAY ATLANTIC UNIVERSITY (the “BAU”) and is effective as of the date
            of Acceptance (the “Effective Date”). It governs your academic and financial rights and responsibilities.
            BAU reserves the right to change or modify portions of this Agreement at any time, effective during the next
            semester’s course registration. You will be asked to sign the Agreement for each semester you are enrolled
            at BAU. This Agreement is entered into by and between Bay Atlantic University (BAU) and the undersigned
            student, participating through the District of Columbia Office of the State Superintendent of Education
            (OSSE) Dual Enrollment Consortium.
          </p>
          <p>
            <strong>This Agreement Covers the Following Current Period of Attendance for the Program:</strong><br>
            Period of Attendance Start Date: {{ registrationForm.education_start_date || '______________' }} Period of
            Attendance End Date: {{ registrationForm.education_end_date || '______________' }}<br>
            <span style="font-style: italic; color: #666;">Semester start and end dates will be entered here</span>
          </p>
          <p>
            This Agreement covers only the above-referenced current period of attendance for the Program. If the number
            of semesters to complete the Program extends beyond the above-referenced period of attendance end date, as a
            condition to further enrollment, you will be required to execute a new Enrollment Agreement with BAU to
            cover the then-current period of attendance for the Program.
          </p>
        </div>
      </div>

      <!-- SECTION A -->
      <hr style="margin: 16px 0;">
      <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">A. VERIFICATION OF PERSONAL
        INFORMATION</h4>
      <p style="font-size: 12px; margin-bottom: 12px;">
        The following information is pulled from the application form you submitted. If you need to update the
        information, you can click on the Edit Button. (Only after this section, there will be an edit section and then
        the student will verify the edited version).
      </p>
      <table
          style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
        <tbody>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; width: 30%; padding: 6px; background-color: #f9f9f9;">Full Name:</td>
          <td style="padding: 6px; width: 35%;">{{ registrationForm.name }}
            {{ registrationForm.middle_name ? registrationForm.middle_name + ' ' : '' }}{{ registrationForm.surname }}
          </td>
          <td style="font-weight: bold; width: 15%; padding: 6px; background-color: #f9f9f9;">Date of Birth:</td>
          <td style="padding: 6px; width: 20%;">
            {{ registrationForm.birthdate ? toLocaleDate(registrationForm.birthdate) : '' }}
          </td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">U.S. Mailing Address:</td>
          <td colspan="3" style="padding: 6px;">
            {{ registrationForm.apartment ? registrationForm.apartment + ', ' : '' }}{{
              registrationForm.contact_address
            }} {{ registrationForm.contact_city_name ? ', ' + registrationForm.contact_city_name : '' }}
            {{ registrationForm.contact_state_name ? ', ' + registrationForm.contact_state_name : '' }}
            {{ registrationForm.contact_zip_code }}
          </td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Your Home Address (International
            Student):
          </td>
          <td colspan="3" style="padding: 6px;">
            {{ registrationForm.address }}
            {{ registrationForm.address_country_name ? ', ' + registrationForm.address_country_name : '' }}
          </td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Your Personal Email:</td>
          <td style="padding: 6px;">{{ registrationForm.personal_email }}</td>
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Your U.S. Mobile Phone:</td>
          <td style="padding: 6px;">{{
              registrationForm.mobile_tel ? (registrationForm.mobile_tel_country_code ?? '') + registrationForm.mobile_tel : ''
            }}
          </td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Other Phone:</td>
          <td style="padding: 6px;">{{ registrationForm.other_phone }}</td>
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Emergency Contact Name:</td>
          <td style="padding: 6px;">{{ registrationForm.emergency_person_name }}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Emergency Contact Relationship to
            You:
          </td>
          <td style="padding: 6px;">{{ registrationForm.emergency_person_relation }}</td>
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Emergency Contact Tel:</td>
          <td style="padding: 6px;">{{
              registrationForm.emergency_person_phone ? (registrationForm.emergency_person_phone_country_code ?? '') + registrationForm.emergency_person_phone : ''
            }}
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Current Legal Presence Type:</td>
          <td colspan="3" style="padding: 6px;">{{ registrationForm.legal_presence_type_text }}</td>
        </tr>
        </tbody>
      </table>
      <p style="font-size: 12px;">☑ I confirm that the information given in this section is true, complete, and
        accurate</p>

      <!-- SECTION B -->
      <hr style="margin: 16px 0;">
      <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">B. PROGRAM INFORMATION</h4>
      <p style="font-size: 12px; margin-bottom: 12px;">The following information is pulled from the application form you
        submitted.</p>
      <table
          style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
        <tbody>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; width: 40%; padding: 6px; background-color: #f9f9f9;">Program of Study:</td>
          <td style="padding: 6px;">{{ programOfStudyText }}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Program Start Date:</td>
          <td style="padding: 6px;">{{ registrationForm.registration_date }}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Registration Type:</td>
          <td style="padding: 6px;">{{ registrationForm.registration_type_text }}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Enrollment Status:</td>
          <td style="padding: 6px;">{{ registrationForm.student_status_text }}</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Total Credits Required for Graduation: <br><small style="font-weight: normal; font-style: italic;">(for students who do not transfer any credit from another institution)</small></td>
          <td style="padding: 6px;">
            <span v-if="activeProgramDatas && activeProgramDatas.length > 1" v-html="totalCreditsFormatted"></span>
            <span v-else-if="registrationForm.total_credit_required_for_graduation && registrationForm.credit">
              {{ registrationForm.total_credit_required_for_graduation }} / {{ registrationForm.credit }}
            </span>
            <span v-else>
              {{ registrationForm.total_credit_required_for_graduation ? registrationForm.total_credit_required_for_graduation : '-' }}
            </span>
          </td>
        </tr>
        </tbody>
      </table>
      <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section B.</p>

      <!-- SECTION C -->
      <hr style="margin: 16px 0;">
      <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">C. FEES AND CHARGES FOR THE
        CURRENT PERIOD OF ATTENDANCE</h4>
      <p style="font-size: 12px; margin-bottom: 12px;">The following fees and charges reflect the information during
        your application process.</p>
      <table
          style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
        <tbody>

        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Tuition per Credit:</td>
          <td style="padding: 6px;">$ {{ registrationForm.tuition_per_credit || 'xxx' }} (The tuition per credit for
            that semester the student is enrolling will be written here)
          </td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">OTHER FEES MAY APPLY:</td>
          <td style="padding: 6px;">For example, requests by students for official university documents (e.g., requests
            for transcripts, enrollment verifications and academic records) may require payment of the relevant fee(s).
            Information about the above-mentioned university fees and other fees can be found on Bay Atlantic
            University’s website at https://bau.edu/admissions/tuition-fees/.
          </td>
        </tr>
        </tbody>
      </table>
      <p style="font-size: 12px;">Tuition typically increases by no more than 3% each academic year. Mandatory semester
        fees increase every two years.</p>
      <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section C.</p>

      <!-- SECTION D -->
      <hr style="margin: 16px 0;">
      <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">D. SCHOLARSHIPS/TUITION
        ASSISTANCE/AWARDS</h4>
      <table
          style="width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; border: 1px solid #ddd;">
        <tbody>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; width: 45%; padding: 6px; background-color: #f9f9f9;">Type of the Scholarship or
            Tuition Assistance Awarded During Admission:
          </td>
          <td style="padding: 6px;">OSSE Dual Enrollment</td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Amount of the Scholarship or Tuition
            Assistance Awarded for the Duration of the Program of Study*:
          </td>
          <td style="padding: 6px;">$ {{ registrationForm.scholarship_amount || 'xxx' }} (Write “0” if the student does
            not have any)
          </td>
        </tr>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; padding: 6px; background-color: #f9f9f9;">Per Credit Amount That Will be Applied
            This Semester:
          </td>
          <td style="padding: 6px;">$ {{ registrationForm.scholarship_per_credit || 'xxx' }}</td>
        </tr>
        </tbody>
      </table>

      <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section D.</p>


      <!-- SECTION G -->
      <hr style="margin: 16px 0;">
      <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">E. OTHER
        AGREEMENTS/UNDERSTANDINGS</h4>
      <ol style="font-size: 12px; padding-left: 20px;">
        <li><strong>ACADEMIC CATALOG:</strong> Academic Catalog contains a description of certain policies, procedures,
          and other information about BAU. BAU reserves the right to change any provision of the catalog at any time.
          The current catalog is available online and can be accessed at the following website address:
          https://bau.edu/academic-catalog/. Students are expected to read and be familiar with the information
          contained in the then-published catalog.
        </li>
        <li><strong>COMPLIANCE WITH POLICIES, PROCEDURES, AND STANDARDS:</strong> BAU’s policies, procedures, and
          standards are published on the website, located at
          https://bau.edu/explore-bau/public-disclosure-policies/all-bau-policies/. Students are expected to know and
          must comply with and adhere to all applicable policies, procedures, and standards...
        </li>
        <li><strong>ACADEMIC CALENDAR:</strong> Students should familiarize themselves with BAU’s calendar for holidays
          and vacations published on the website at https://bau.edu/academic-calendar/...
        </li>
        <li><strong>DUAL ENROLLMENT STATUS DISCLOSURE:</strong> The student is enrolled as a non-degree, special
          undergraduate student through the OSSE Dual Enrollment Consortium. Participation does not constitute admission
          to a BAU degree program and does not guarantee future admission.
        </li>
        <li><strong>NOTICE CONCERNING TRANSFERABILITY OF CREDITS AND CREDENTIALS EARNED AT BAU:</strong> The
          transferability of credits you earn at BAU is at the complete discretion of the institution to which you may
          seek to transfer...
        </li>
        <li><strong>CAREER SERVICES:</strong> Guidance and professional development opportunities are available
          in-person and virtually. BAU is, however, unable to guarantee employment, does not provide placement, and
          makes no promises with regard to level of income or visa sponsorship/change of status for international
          students.
        </li>
        <li><strong>IMPLIED CONSENT:</strong> You acknowledge and agree that BAU or its representatives, affiliates,
          successors, and related companies may photograph, video or otherwise record you and/or other students...
        </li>
        <li><strong>COMPLAINTS:</strong> A student or any member of the public may file a complaint about this
          institution on issues that cannot be resolved by direct negotiation with the school in accordance with its
          written grievance policy with the Higher Education Licensure Commission (HELC)...
        </li>
        <li><strong>LEGAL:</strong> The law, including the statutes of limitation, of the District of Columbia will
          govern this Agreement, the interpretation and enforcement of its terms and any claim or cause of action...
        </li>
        <li><strong>COPY OF THE ENROLLMENT AGREEMENT:</strong> A copy of this Contract will be emailed to the student
          upon signing and will be accessible on the student’s MyBAU account.
        </li>
      </ol>
      <p style="font-size: 12px;">☑ I read and confirm that I understand the information in Section E.</p>

      <!-- FINAL ACKNOWLEDGEMENT & ACCEPTANCE -->
      <hr style="margin: 16px 0;">
      <h4 style="font-size: 14px; font-weight: bold; margin: 16px 0 8px 0; color: #333;">FINAL ACKNOWLEDGEMENT &amp;
        CONTRACT ACCEPTANCE</h4>
      <p style="font-size: 12px;">
        I acknowledge and agree that the terms and conditions of this enrollment agreement are not subject to amendment
        or modification by oral agreement. I further acknowledge and agree that no oral statements have been made to me
        that are contrary to what is contained in this enrollment agreement. My signature below certifies that I have
        read, understand and agree to the terms and conditions contained in this enrollment agreement. This enrollment
        agreement is legally binding when signed by me and accepted by Bay Atlantic University.
      </p>
      <p style="font-size: 12px;"><strong>CONTRACT ACCEPTANCE</strong></p>
      <p style="font-size: 12px;">
        I, the undersigned, have read and understood this agreement and agree that it supersedes all prior or
        contemporaneous verbal or written agreements and may not be modified without the written agreement of both the
        student and the School Official. I also understand that if I default on this agreement, I will be responsible
        for paying any collection fees or attorney fees incurred by BAU.
      </p>
      <p style="font-size: 12px;">
        By clicking the box below, I hereby affix my electronic signature, indicating my intent to be legally bound by
        the terms of this agreement. My signature below signifies that I have read, understood, and acknowledged all
        aspects of this agreement, and I have recognized my legal responsibilities under this contract.
      </p>

      <table style="width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 12px; border: 1px solid #ddd;">
        <tbody>
        <tr style="border-bottom: 1px solid #ddd;">
          <td style="font-weight: bold; width: 60%; padding: 8px;">[{{ registrationForm.name }}
            {{ registrationForm.middle_name ? registrationForm.middle_name + ' ' : '' }}{{ registrationForm.surname }}]
          </td>
          <td style="font-weight: bold; padding: 8px;">Date: {{ currentDate }}</td>
        </tr>

        </tbody>
      </table>
    </div>

    <div style="display: flex; justify-content: flex-end;" class="no-button mt-3">
      <b-button variant="primary" @click="submitData" :disabled="loading">
        <b-spinner small v-if="loading"></b-spinner>
        {{ $t('send') }}
      </b-button>
    </div>
  </div>
</template>
<script>
import moment from 'moment';
import EnrollmentAgreementForm from "@/services/EnrollmentAgreementForm";
import {mapGetters} from "vuex";

export default {
  data() {
    return {
      disabled:false,
      loading:false,
      registrationForm: {},
      data:[],
      activeProgramDatas: [],
    }
  },
  computed: {
    ...mapGetters({
      studentData: "enrollmentAgreementForm/formData"
    }),
    currentDate() {
      return moment().format("MMMM Do YYYY")
    },
    programOfStudyText() {
      if (this.activeProgramDatas && this.activeProgramDatas.length > 1) {
        return this.activeProgramDatas.map(p => p.program_name).filter(Boolean).join(', ');
      }
      return this.registrationForm.program_name ? this.registrationForm.program_name : '-';
    },
    totalCreditsFormatted() {
      if (this.activeProgramDatas && this.activeProgramDatas.length > 1) {
        return this.activeProgramDatas.map((p, i) => {
          const cred = p.total_credit_required_for_graduation;
          const comma = i < this.activeProgramDatas.length - 1 ? ',' : '';
          return `${cred} credits for ${p.program_name}${comma}`;
        }).join('<br>');
      }
      return '';
    }
  },
  created() {
    this.getData()
  },
  methods: {
    async submitData() {
      this.loading = true;
      try {
        await this.sendToBackend();
      } catch (error) {
        console.error('Error in submitData:', error);
      } finally {
        this.loading = false;
      }
    },
    async getData() {
      const response = await EnrollmentAgreementForm.getStudentInformation(this.studentData.id)
      const data = response.data.data;

      let user = await JSON.parse(localStorage.getItem('user'));
      let studentPrograms = user?.student?.student_program || [];
      let activeProgs = studentPrograms.filter(p => ['100', '101'].includes(p.student_status));

      if (activeProgs.length > 1) {
        try {
          const responses = await Promise.all(
              activeProgs.map(p => EnrollmentAgreementForm.getStudentInformation(p.id))
          );
          this.activeProgramDatas = responses.map(res => res.data.data);
        } catch (e) {
          console.error('Error fetching active program details:', e);
          this.activeProgramDatas = activeProgs;
        }
      } else {
        this.activeProgramDatas = [];
      }

      this.data.push(
          {
            key: 'Admission Confirmation Deposit',
            value: `$${data.admission_confirmation_deposit ? data.admission_confirmation_deposit : 0} – Non Refundable (but credited towards tuition).`,
          },
          {
            key: 'Tuition Deposit',
            value: `$${data.tuition_deposit ? data.tuition_deposit : 0} – Non Refundable (but credited towards tuition).`,
          },
          {
            key: 'Tuition per Credit ',
            value: `$${data.tuition_per_credit ? data.tuition_per_credit : 0}`,
          },
          {
            key: 'MANDATORY FEES',
            value: `Student Activities and Services Fee: $${data.services_fee}<br>Technology Fee: $${data.technology_fee}<br>Student ID Fee: $${data.student_id_card_fee ?? 0} (only if this is your first semester at BAU)`,
          },
          {
            key: 'OTHER FEES MAY APPLY',
            value: `For example, requests by students for official university documents (e.g., requests for transcripts, enrollment verifications and academic records) may require payment of the relevant <strong>university fees</strong>. Information about the above-mentioned <strong>university fees</strong> and other fees can be found on <a href="https://bau.edu/admissions/tuition-fees/" target="_blank">Bay Atlantic University's website</a>.`,
          },
      )
      this.registrationForm = {
        ...this.studentData,
        registration_date: data.registration_date,
        program_name: data.program_name,
        registration_semester_academic_year: data.registration_semester_academic_year,
        anticipated_end_date: data.anticipated_end_date,
        registration_type_text: data.registration_type_text,
        total_credit_required_for_graduation: data.total_credit_required_for_graduation,
        tuition_per_credit: data.tuition_per_credit,
        total_discount_per_credit: data.discount_per_credit * data.total_credit_required_for_graduation
      }
    },
    async sendToBackend() {
      const config = {
        // html: base64String,
        contact_address: this.studentData.contact_address,
        contact_state_id: this.studentData.contact_state_id,
        contact_city_id: this.studentData.contact_city_id,
        contact_zip_code: this.studentData.contact_zip_code,
        apartment: this.studentData.apartment,
        address_country_id: this.studentData.address_country_id,
        address: this.studentData.address,
        verification_code: this.studentData.verification_code,
        personal_email: this.studentData.personal_email,
        mobile_tel: this.studentData.mobile_tel,
        mobile_tel_country_code: this.studentData.mobile_tel_country_code,
        home_tel: this.studentData.home_tel,
        home_tel_country_code: this.studentData.home_tel_country_code,
        emergency_person_phone_country_code: this.studentData.emergency_person_phone_country_code,
        emergency_person_relation: this.studentData.emergency_person_relation,
        emergency_person_name: this.studentData.emergency_person_name,
        emergency_person_phone: this.studentData.emergency_person_phone,
        birthdate: this.studentData.birthdate,
      }
      try {
        const response = await EnrollmentAgreementForm.sendForm(config);
        if (response.data.success === true) {
          this.$toast.success(this.$t('form_submission_successful'))
          this.$emit('sendSuccessForm', true)
          this.clearForm()
          this.$router.push('/dashboard');
        } else {
          this.$toast.success(this.$t('api.' + response.data.message));
          this.clearForm()
        }
      } catch (error) {
        if (error.data.message === "SMS_CODE_ERROR") {
          this.$toast.error(this.$t('api.' + error.data.message));
          this.$emit('errorSmsCode', true)

        } else {
          this.$toast.error(this.$t('api.' + error.data.message));
        }
      }
    },
    async clearForm() {
      await this.$store.dispatch('enrollmentAgreementForm/deleteFormData');
    },

  }
}
</script>
<style scoped>
@media print {
  body {
    -webkit-print-color-adjust: exact !important;
  }

  .no-button {
    display: none;
  }
}
</style>
